<script setup lang="ts">
  import { reactive, computed, onMounted, watch } from "vue";
  import { useI18n } from "vue-i18n";
  import { IconArrowLeft, IconFilter, IconSort } from "@ui-kit";
  import { shopZoneListWithCursorApi, type StatRange } from "@src/api";
  import { useShopZoneListStore, useUserInfoStore } from "@src/store";
  import { Widget, StatusBar, TopBar, ButtonGlass } from "@src/components";
  import { NumberService } from "@src/services";
  import { utils, type MoneymapItemWithTitle } from "./utils";
  import { default as MoneymapItem } from "./MoneymapItem.vue";
  import { default as MoneymapFilterModal } from "./MoneymapFilterModal.vue";
  import { default as MoneymapSortModal } from "./MoneymapSortModal.vue";

  const emit = defineEmits<{ (e: "close"): void }>();

  const state = reactive<{
    current_zone_id: string;
    data: MoneymapItemWithTitle[];
    filter_modal_visible: boolean;
    selected_range_id: string;
    sort_modal_visible: boolean;
    selected_sort_id: string;
    selected_display_graph_id: string;
  }>({
    current_zone_id: "noop",
    data: [],
    filter_modal_visible: false,
    selected_range_id: "today_0",
    sort_modal_visible: false,
    selected_sort_id: "default",
    selected_display_graph_id: "all",
  });

  const i18n = useI18n();
  const shop_zone_list_store = useShopZoneListStore();
  const user_info_store = useUserInfoStore();

  const zone = computed(() => shop_zone_list_store.map[state.current_zone_id]);
  const subtitle = computed(() => {
    const { sum, count } = state.data.reduce<{ sum: number; count: number }>(
      (acc, cur) => {
        acc.sum += Number.parseInt(cur.amount_total);
        acc.count += cur.quantity_total;

        return acc;
      },
      { sum: 0, count: 0 },
    );

    return `${NumberService.toLocaleCurrency(sum)}, ${count} ${i18n.t("шт")}.`;
  });
  const filter_counter = computed(() => {
    let count = 0;

    if (state.selected_display_graph_id !== "all") count++;
    if (state.selected_range_id !== "today_0") count++;

    return count;
  });

  const moneymapLoad = async () => {
    const [range, delta] = state.selected_range_id.split("_");

    const data = await utils.fetchMoneymapWithZones(
      {
        shop_id: user_info_store.shop.shop_id,
        range: range as StatRange,
        zone_id: state.current_zone_id !== "noop" ? state.current_zone_id : undefined,
        delta: delta ? Number.parseInt(delta) : undefined,
      },
      shop_zone_list_store,
    );

    if (data === null) return;

    const zone = shop_zone_list_store.map[state.current_zone_id];
    if (!zone) {
      state.data = [];
      return;
    }

    const merged = utils.mergeZoneMoneymapData(zone, data, shop_zone_list_store.map);
    state.data =
      state.current_zone_id === "noop"
        ? utils.sortMoneymapByDepartments(merged)
        : utils.sortMoneymapByDepartments(merged);
  };

  const handleClickBack = () => {
    if (zone.value.parent_id) {
      state.current_zone_id = zone.value.parent_id;
    } else {
      emit("close");
    }
  };

  watch(() => state.current_zone_id, moneymapLoad);
  watch(() => state.selected_range_id, moneymapLoad);

  onMounted(async () => {
    if (shop_zone_list_store.is_empty) {
      const result = await shopZoneListWithCursorApi();

      shop_zone_list_store.add(result);
    }

    await moneymapLoad();
  });
</script>

<template>
  <Widget>
    <div class="moneymap" data-testid="moneymap">
      <StatusBar />
      <TopBar
        :title="state.current_zone_id === 'noop' ? $t('Money mapping') : zone.title"
        :subtitle="state.current_zone_id === 'noop' ? undefined : subtitle"
        @click:left="handleClickBack"
      >
        <template #left>
          <IconArrowLeft />
        </template>
      </TopBar>
      <div class="moneymap__item-list" data-testid="moneymap-item-list">
        <MoneymapItem
          v-for="item in state.data"
          :key="item.zone_id"
          :zone_id="item.zone_id"
          :shop_id="user_info_store.shop.shop_id"
          :title="item.title"
          :amount="item.amount_total"
          :quantity="item.quantity_total"
          :is_zone="item.is_zone"
          :is_link="item.is_link"
          :selected_range_id="state.selected_range_id"
          :selected_sort_id="state.selected_sort_id"
          :selected_display_graph_id="state.selected_display_graph_id"
          @click="state.current_zone_id = item.zone_id"
        />
      </div>
      <div class="moneymap__action-container">
        <ButtonGlass @click="state.sort_modal_visible = true" data-testid="moneymap-btn-sort">
          <IconSort />
        </ButtonGlass>
        <ButtonGlass
          :counter="filter_counter || undefined"
          @click="state.filter_modal_visible = true"
          data-testid="moneymap-btn-filter"
        >
          <IconFilter />
        </ButtonGlass>
      </div>
    </div>
  </Widget>
  <MoneymapFilterModal
    v-if="state.filter_modal_visible"
    :selected_range_id="state.selected_range_id"
    :selected_display_graph_id="state.selected_display_graph_id"
    @apply="
      (payload) => {
        state.selected_range_id = payload.range_id;
        state.selected_display_graph_id = payload.display_graph_id;
      }
    "
    @close="state.filter_modal_visible = false"
  />
  <MoneymapSortModal
    v-if="state.sort_modal_visible"
    :selected_sort_id="state.selected_sort_id"
    @close="state.sort_modal_visible = false"
    @click:item="state.selected_sort_id = $event"
  />
</template>

<style scoped>
  .moneymap {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    position: relative;
  }

  .moneymap__item-list {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    gap: 8px;
    padding: 16px 8px 64px;
  }

  .moneymap__action-container {
    display: flex;
    gap: 4px;
    position: absolute;
    bottom: 16px;
    right: 16px;
  }
</style>
